//Libs
import React, { useEffect } from "react";
import Intercom from "@intercom/messenger-js-sdk";
import styled from "styled-components";

// Global stylesheet — carries the @font-face declarations for Avenir. It is
// only imported by a handful of components app-wide (Navigation, Register),
// not from src/index.js, so this page cannot assume Avenir is already
// registered: it may render before any of those ever mount (e.g. the outage
// gate showing this over a fresh page load).
import "index.css";

import paths from "routes/paths";

// Constants
import { APP_ASSETS_BUCKET_URL } from "constants/common";

// images
const logo = `${APP_ASSETS_BUCKET_URL}/new-spocket-logo.svg`;

/**
 * Maintenance page — restyled per Figma node 150:2734 ("config profile B":
 * Spocket + DropshipTool share this glow/pill/font configuration, distinct
 * from the other eight maintenance-page ports).
 *
 * Deliberately self-contained: no shared layout, no data fetching beyond
 * Intercom. This renders in two situations — routed directly at
 * paths.app.maintenance, and mounted as an overlay by the outage-detection
 * gate in src/index.js while the real app stays mounted (hidden) underneath
 * it — so anything this page depends on is something that could stop it
 * rendering at exactly the wrong moment.
 *
 * Colors below are sampled directly from the Figma frame, not the app theme:
 * the design uses a blue-tinted neutral ink (#081F40 / #767797) that is
 * specific to this page. Only the accent (#7020FF) doubles as the app's own
 * brand purple (theme colorPurple15).
 */
const ACCENT = "#7020FF";
const INK_HEADLINE = "#081F40";
const INK_BODY = "#767797";
/* The divider and card border are the headline ink at low alpha, not a shared
   neutral. Hex alpha: 17 = 9%, 12 = 7%. */
const INK_DIVIDER = `${INK_HEADLINE}17`;
const INK_CARD_BORDER = `${INK_HEADLINE}12`;
/* Status pill border alpha — Spocket/DropshipTool use 14%, not the 27% most
   of the other maintenance-page ports use. Hex alpha: 24 = 14%. Precomputed
   into one constant (rather than interpolated inline) because prettier
   inserts a space between two adjacent `${}` template expressions, which
   would silently corrupt this into an invalid two-token color value. */
const PILL_BORDER = `${ACCENT}24`;

const TABLET_BP = "768px";

/**
 * Everything the DropshipTool cross-promo needs. Spocket only has to render
 * itself, so — unlike the multi-app reference this was ported from — this is
 * inlined here rather than split into a separate content.ts shared by ten
 * apps.
 */
const PROMO = {
  name: "Dropshiptool",
  headline: "Discover Winning Products and High-Performing Viral Ads",
  blurb:
    "Track over 3 million Shopify stores to uncover winning products, viral TikTok ads, and top-performing Facebook and Instagram creatives.",
  linkLabel: "dropshiptool.io",
  linkUrl:
    "https://app.dropshiptool.io/register?utm_source=spocket&utm_medium=maintenance&utm_campaign=findwinningads"
};

/* Byte-identical to the other nine apps' SHARED block. The headline, body and
   CTA used to come from the Maintenance.MainMessage / Maintenance.GoBackHome
   i18n keys, which carried this page's pre-redesign wording ("...will be back
   soon!", "Go Back Home", and a different body sentence) and so rendered copy
   the design does not specify. The keys are left in en.json untouched for
   anything else that uses them; this page now renders the specified strings
   directly, the same way it already did for the status pill, the reassurance
   line and the whole promo block. */
const COPY = {
  status: "Maintenance in progress",
  statusShort: "Maintenance",
  headline: "Spocket will be back soon.",
  body:
    "We're running scheduled work on the platform. Nothing is lost, and your store keeps selling. We'll be back shortly.",
  cta: "Back to Home",
  reassurance: "This page refreshes itself when we're live.",
  promoEyebrow: "While you wait"
};

const Page = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2000;
  overflow-y: auto;
  /* The rings extend past the right edge; without this the browser resolves
     overflow-x to auto and the page scrolls sideways. */
  overflow-x: hidden;
  background: #ffffff;
  /* 1728x808 at 82%, opacity 1.0, stop rgba(236,230,255,1) — verified on the
     Figma frame (node 150:2734). */
  background-image: radial-gradient(
    ellipse 1728px 808px at 82% 8%,
    rgba(236, 230, 255, 1) 0%,
    rgba(236, 230, 255, 0) 58%
  );
  font-family: Avenir, Nunito, sans-serif;
  color: ${INK_HEADLINE};

  @media (max-width: ${TABLET_BP}) {
    background-image: radial-gradient(
      ellipse 426px 808px at 82% 8%,
      rgba(236, 230, 255, 1) 0%,
      rgba(236, 230, 255, 0) 58%
    );
  }

  /* Two concentric rings bleeding off the top-right (Figma Ellipse 66/67).
     Spocket places them at 890/834 of a 1440 frame (61.8%/57.9%) — 109px
     further right than most of the other maintenance-page ports. */
  &::before,
  &::after {
    content: "";
    position: absolute;
    border-radius: 50%;
    pointer-events: none;
  }
  /* Ellipse 66 is 0.1 opacity, Ellipse 67 is 0.05 — collapsing them to one
     value renders the outer ring twice as strong as designed. Hex alpha:
     1A = 10%, 0D = 5%. */
  &::before {
    width: 793px;
    height: 793px;
    left: 61.8%;
    top: -25.3%;
    border: 1px solid ${ACCENT}1A;
  }
  &::after {
    width: 905px;
    height: 905px;
    left: 57.9%;
    top: -31.5%;
    border: 1px solid ${ACCENT}0D;
  }

  @media (max-width: ${TABLET_BP}) {
    /* Same circles, re-anchored for the mobile frame — not hidden. */
    &::before {
      left: 55.8%;
    }
    &::after {
      left: 41.4%;
    }
  }
`;

const Inner = styled.div`
  position: relative;
  z-index: 1;
  max-width: 1332px;
  margin: 0 auto;
  min-height: 100%;
  padding: 54px 54px 40px;
  display: flex;
  flex-direction: column;

  @media (max-width: ${TABLET_BP}) {
    padding: 28px 18px 32px;
  }
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

const Logo = styled.img`
  height: 35.67px;
  width: 123px;
  /* Rendered with object-fit: contain, so a wrong value letterboxes rather
     than distorts. */
  object-fit: contain;
  object-position: left center;

  @media (max-width: ${TABLET_BP}) {
    height: 29px;
    width: 108px;
  }
`;

const Status = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 9.33px;
  padding: 7.13px 14.26px;
  border: 1.019px solid ${PILL_BORDER};
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.75);
  font-size: 13.24px;
  letter-spacing: 0.265px;
  color: ${INK_BODY};
  white-space: nowrap;

  &::before {
    content: "";
    width: 6.81px;
    height: 6.81px;
    border-radius: 3.4px;
    background: ${ACCENT};
    opacity: 0.833;
    flex: none;
  }
`;

const StatusFull = styled.span`
  @media (max-width: ${TABLET_BP}) {
    display: none;
  }
`;

const StatusShort = styled.span`
  display: none;
  @media (max-width: ${TABLET_BP}) {
    display: inline;
  }
`;

const Main = styled.main`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 80px 0;

  @media (max-width: ${TABLET_BP}) {
    padding: 56px 0 40px;
    align-items: center;
    text-align: center;
  }
`;

/* A real <h1>: this was a <span> only because the old i18n string nested
   the body inside the headline, which is valid HTML only if both are
   phrasing content. Rendering the strings directly lifts that constraint,
   so the page now has a heading for assistive tech like its nine siblings. */
const Title = styled.h1`
  display: block;
  margin: 0;
  font-size: 56px;
  line-height: 62px;
  font-weight: 800; /* Avenir Heavy */
  letter-spacing: -2.66px;

  @media (max-width: 1024px) {
    text-wrap: balance;
  }
  @media (max-width: ${TABLET_BP}) {
    font-size: 42px;
    line-height: 52px;
  }
  @media (max-width: 375px) {
    font-size: 36px;
    line-height: 44px;
  }
`;

const Body = styled.p`
  display: block;
  margin: 22px 0 0;
  max-width: 640px;
  font-size: 18px;
  line-height: 30px;
  letter-spacing: 0.027px;
  font-weight: 500; /* Avenir Medium */
  color: ${INK_BODY};

  @media (max-width: ${TABLET_BP}) {
    margin-left: auto;
    margin-right: auto;
  }
`;

const Cta = styled.a`
  margin-top: 22px;
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 15px 32px;
  border-radius: 8px;
  box-shadow: 0 10px 12px ${ACCENT}1F;
  background: ${ACCENT};
  color: #ffffff;
  font-size: 16px;
  font-weight: 900; /* Avenir Black */
  letter-spacing: 0.15px;
  text-decoration: none;
  transition: filter 0.15s ease;

  &:hover {
    filter: brightness(0.94);
  }
  &:focus-visible {
    outline: 2px solid ${INK_HEADLINE};
    outline-offset: 3px;
  }

  @media (max-width: ${TABLET_BP}) {
    align-self: stretch;
    justify-content: center;
    font-weight: 600;
  }
`;

const Reassurance = styled.p`
  margin: 18px 0 0;
  font-size: 14px;
  font-weight: 500; /* Avenir Medium */
  color: ${INK_BODY};

  @media (max-width: ${TABLET_BP}) {
    font-weight: 400;
  }
`;

const Promo = styled.section`
  border-top: 1px solid ${INK_DIVIDER};
  padding-top: 26px;

  @media (max-width: ${TABLET_BP}) {
    padding-top: 32px;
  }
`;

const PromoCard = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px 40px;
  align-items: center;
  padding: 20px 24px;
  border: 1px solid ${INK_CARD_BORDER};
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.8);

  @media (max-width: 1024px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
`;

const PromoLeft = styled.div`
  min-width: 210px;
`;

const PromoMiddle = styled.div`
  flex: 1 0 0;
  min-width: 260px;
`;

const PromoEyebrow = styled.p`
  margin: 0 0 5px;
  font-size: 11px;
  font-weight: 800; /* Avenir Heavy */
  letter-spacing: 1.76px;
  text-transform: uppercase;
  color: ${INK_BODY};

  @media (max-width: ${TABLET_BP}) {
    font-weight: 400;
  }
`;

const PromoName = styled.p`
  margin: 0;
  font-size: 19px;
  font-weight: 800; /* Avenir Heavy */
  letter-spacing: -0.285px;
`;

const PromoHeadline = styled.p`
  margin: 0 0 4px;
  font-size: 15px;
  font-weight: 800; /* Avenir Heavy */

  @media (max-width: ${TABLET_BP}) {
    margin: 0;
    line-height: 22px;
  }
`;

const PromoBlurb = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 21px;
  color: ${INK_BODY};
`;

const PromoLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 800; /* Avenir Heavy */
  color: ${ACCENT};
  text-decoration: underline;
  text-underline-offset: 3px;
  white-space: nowrap;

  &:focus-visible {
    outline: 2px solid ${INK_HEADLINE};
    outline-offset: 3px;
  }
`;

const ArrowLeft = () => (
  <svg width="24" height="12" viewBox="0 0 24 12" fill="none" aria-hidden="true">
    <path
      d="M16.945 0.2432C16.8676 0.3197 16.8061 0.4107 16.7642 0.5109C16.7222 0.6112 16.7007 0.7187 16.7007 0.8273C16.7007 0.9359 16.7222 1.0434 16.7642 1.1437C16.8061 1.2439 16.8676 1.3349 16.945 1.4114L20.7444 5.1957H1.30123C1.08217 5.1957 0.87208 5.2824 0.71718 5.4367C0.56228 5.5909 0.47526 5.8002 0.47526 6.0184C0.47526 6.2366 0.56228 6.4458 0.71718 6.6001C0.87208 6.7544 1.08217 6.841 1.30123 6.841H20.7279L16.945 10.6007C16.7911 10.7548 16.7048 10.9633 16.7048 11.1806C16.7048 11.398 16.7911 11.6065 16.945 11.7606C17.0997 11.9138 17.3091 11.9999 17.5273 11.9999C17.7455 11.9999 17.9548 11.9138 18.1096 11.7606L23.3628 6.5284C23.4334 6.4609 23.4897 6.3798 23.5281 6.29C23.5666 6.2002 23.5864 6.1036 23.5864 6.006C23.5864 5.9084 23.5666 5.8118 23.5281 5.7221C23.4897 5.6323 23.4334 5.5512 23.3628 5.4836L18.1179 0.2432C18.0411 0.1661 17.9497 0.1049 17.8491 0.0631C17.7484 0.0214 17.6404 -0.0001 17.5315 -0.0001C17.4224 -0.0001 17.3144 0.0214 17.2138 0.0631C17.1132 0.1049 17.0218 0.1661 16.945 0.2432Z"
      fill="currentColor"
      transform="rotate(180 12 6)"
    />
  </svg>
);

const ArrowOut = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
    style={{ transform: "rotate(-90deg)" }}
  >
    <path
      d="M10.6369 4.5666C10.5774 4.5705 10.5173 4.5862 10.4602 4.6127C10.403 4.6392 10.3498 4.676 10.3038 4.7209C10.2577 4.7659 10.2196 4.8181 10.1917 4.8746C10.1638 4.9311 10.1467 4.9908 10.1413 5.0501L9.86284 7.9765L3.07252 1.0171C2.99602 0.9386 2.88588 0.8993 2.76633 0.9077C2.64679 0.9162 2.52763 0.9716 2.43507 1.0619C2.34251 1.1522 2.28413 1.27 2.27278 1.3893C2.26143 1.5086 2.29803 1.6197 2.37454 1.6981L9.15908 8.6516L6.24303 8.8537C6.12392 8.8624 6.00532 8.9178 5.91312 9.0078C5.82091 9.0977 5.76261 9.2149 5.75096 9.3338C5.74001 9.4526 5.77662 9.5631 5.85283 9.6412C5.92904 9.7194 6.03864 9.7587 6.15769 9.7506L10.2119 9.4653C10.2652 9.4626 10.3193 9.4492 10.3708 9.4258C10.4223 9.4024 10.4702 9.3695 10.5116 9.3291C10.553 9.2887 10.5871 9.2416 10.6117 9.1907C10.6364 9.1398 10.6511 9.0861 10.6551 9.0328L11.0465 4.9864C11.0524 4.927 11.0464 4.869 11.029 4.8157C11.0116 4.7624 10.983 4.7148 10.9449 4.6758C10.9068 4.6368 10.86 4.607 10.8071 4.5883C10.7543 4.5696 10.6964 4.5622 10.6369 4.5666Z"
      fill="currentColor"
    />
  </svg>
);

export const Maintenance = () => {
  /* In an effect and guarded, deliberately. This ran in the render body, so a
     throw from either the storage read (blocked in some privacy modes) or the
     Intercom SDK (itself a network dependency, and just as likely to be
     degraded during the outage this page exists for) took the whole component
     down with it — leaving a blank screen on the one page that has to render
     unconditionally. It also re-fired on every re-render. */
  useEffect(() => {
    try {
      Intercom({
        app_id: process.env.REACT_APP_INTERCOM_KEY,
        email: localStorage.user_email
      });
    } catch {
      // Support chat is a nicety here; never let it stop the page rendering.
    }
  }, []);

  return (
    <Page role="alert">
      <Inner>
        <Header>
          <Logo src={logo} alt="Spocket" className="maintenance__spocket-logo" />
          <Status>
            <StatusFull>{COPY.status}</StatusFull>
            <StatusShort>{COPY.statusShort}</StatusShort>
          </Status>
        </Header>

        <Main>
          <Title>{COPY.headline}</Title>
          <Body>{COPY.body}</Body>
          {/* A plain anchor, not a router Link/useNavigate: this page can
              render OUTSIDE the router (the outage gate mounts it as a
              sibling of <Routes /> in src/index.js, which owns the only
              RouterProvider — see routes/index.js), and a full navigation is
              the intent anyway when the app itself may be what's
              unreachable. */}
          <Cta href={paths.app.index}>
            <ArrowLeft />
            {COPY.cta}
          </Cta>
          <Reassurance>{COPY.reassurance}</Reassurance>
        </Main>

        <Promo>
          <PromoCard>
            <PromoLeft>
              <PromoEyebrow>{COPY.promoEyebrow}</PromoEyebrow>
              <PromoName>{PROMO.name}</PromoName>
            </PromoLeft>
            <PromoMiddle>
              <PromoHeadline>{PROMO.headline}</PromoHeadline>
              <PromoBlurb>{PROMO.blurb}</PromoBlurb>
            </PromoMiddle>
            <PromoLink href={PROMO.linkUrl} target="_blank" rel="noopener noreferrer">
              {PROMO.linkLabel}
              <ArrowOut />
            </PromoLink>
          </PromoCard>
        </Promo>
      </Inner>
    </Page>
  );
};
